/**
 * The units a re-solve KEEPS, brought to the nearest unit a vendor line makes.
 *
 * Re-solving a run in a line with a size catalog (`spec.sizes` — Venus)
 * already packs every box the FILL builds in the line's sizes. What it does
 * not touch are the incumbents — the bodies the run engine re-seats rather
 * than rebuilds, because they carry what the scan measured (`incumbents.js`):
 * the tall units, the sink's host cabinet, and the measured uppers a keep
 * policy leaves where they hang (the over-fridge cabinet). Left alone they
 * kept their measured sizes and priced as missing.
 *
 * Two callers, ONE rule:
 *   - the kitchen generator's "match existing" option under the company's
 *     cabinet line (`web-app/src/services/kitchen/minimalChangeLayout.js`);
 *   - a run switched into the line by hand (`applyRunFamily` in
 *     `web-app/src/utils/cabinetRunGeometry.js`) — which also re-solves a run
 *     ALREADY labelled with the line when it still holds units the line does
 *     not make (`runHoldsUnitsOutsideLine`): a run switched before the line
 *     could size its install pieces (the Venus fridge column, 2026-10-08)
 *     carried a ½" surround at the fridge's depth and a 13"-tall over-fridge
 *     cabinet, and a stamp-only re-pick could never repair it.
 *
 * Every answer here is the LINE's own rule, never a size written down:
 *   - a tall unit's width is the nearest pantry width the family makes
 *     (`snapTallWidthToFamily`), set on the UNIT before the re-solve: the
 *     engine sizes a tall unit's bay from the unit itself
 *     (`resolveRunBayFootprints`), so the bay, the packing around it and the
 *     re-seated unit all follow — and its height is the one the fill stands a
 *     tall span at (`tallSpanSpec`: the tallest the family makes under the
 *     line);
 *   - a tall unit is a PANTRY (`applyBayType(…, 'tall')`, the fill's own tall
 *     front) — a measured body carries no `category.tall`, and without it the
 *     catalog reads an 84" pantry as a base;
 *   - the sink's host is a SINK base — the scanned host carries no
 *     `category.sinkBase`, and without it the catalog reads it as a plain base;
 *   - every kept unit wears the run's look in the line (`lookForNewRun`, the
 *     look a generated island takes) — the scan's own colour is no colour
 *     the line sells;
 *   - a kept upper is the catalog unit of its own kind (`cabinetKindOf`) the
 *     fill would hang in its slot — the widest that fits (`fitUpperWidthIn`) at
 *     the tallest that fits (`snapUpperHeightIn`), top on its line, back on its
 *     wall, at the unit's own depth — or none, as the fill builds none.
 *
 * An OVEN COLUMN is exempt throughout: its sections are cut at the oven's
 * underside, so a section brought to a line height would leave the appliance
 * floating over its own housing or open a gap above it.
 *
 * Pure: every function returns a new object and never touches its argument.
 */
const {
  familyFillOpts,
  snapTallWidthToFamily,
  cornerLSet,
} = require('./families');
const { tallSpanSpec } = require('./fillRun');
const { isReseatedTallUnit, sinkHostIdOf } = require('./incumbents');
const { baysForRun } = require('./bays');
const { lookForNewRun, wearLook } = require('./newRunFamily');
const { applyBayType, tierOf } = require('./bayOverrides');
const {
  cabinetKindOf,
  familySizes,
  fitUpperWidthIn,
  resolveCabinetSku,
  snapUpperHeightIn,
  unitsOf,
} = require('./vendorSizes');
const {
  STANDARD_CABINET_DEPTH,
  // lives beside its threshold so cabinet trim can ask it without this module
  isTallBody,
} = require('./runDepth');
const { isOvenColumnPart } = require('./ovenColumn');
const { upperBandFor, columnTopY } = require('./upperBand');
const { roomCeilingY } = require('./materializeRun');
const { getFloorY } = require('../vanity-assembly/placementUtils');
const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { mayResizeObject } = require('../fixture-types/productRepresentation');

const IN = 0.0254;

/**
 * The kept bodies that wear the line's look: the cabinetry a re-solve keeps.
 * Appliances keep their own.
 */
const KEPT_IN_LINE_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet',
  'corner-fill-cabinet',
  'fridge-surround',
]);

/** Kept uppers the line sizes: a plain wall cabinet and the deep bridge. */
const SIZED_UPPER_KINDS = new Set(['wall', 'wall-bridge-24']);

/** A tall cabinet body a line may stand to its line — never an oven column. */
const isLineTallBody = (o) =>
  isTallBody(o) && o.type === 'lower-cabinet' && !isOvenColumnPart(o);

/**
 * A tall unit at the standard depth and height: back face left on its wall,
 * bottom left on the floor.
 *
 * The wall's inward normal is the axis a cabinet is seated along
 * (`buildLowerCabinetForSegment` pushes the centre in by half the depth to put
 * the back flush), so re-depthing means walking the centre along it by half
 * the difference. The height is the run's UPPER TOP LINE — not an 84" written
 * down here, but the top of the run's own band (`upperBandFor`), the line
 * `fillRun`'s own `tallSpanSpec` builds a tall span to, so a standardized
 * pantry dies into the same top as the uppers beside it. It was
 * `upperTopLineY`, which IS an 84" written down: a run hung to the ceiling at
 * 42" stood its uppers at 96" and its pantry a foot short of them.
 *
 * Returns the object unchanged when it has no wall to measure against — a
 * free-standing unit has no back and no line to stand to.
 *
 * `depth` is the standard unless the caller knows better: a tall FILLER is as
 * deep as the column it stands against (`fridgeColumnSpec`), and squared to
 * 24" it stood short of a 30" fridge column.
 */
function standardizedTallBody(
  o,
  wall,
  floorY,
  topLineY,
  depth = STANDARD_CABINET_DEPTH
) {
  const axes = wall ? getWallAxes(wall) : null;
  const dims = o?.dimensions;
  if (!axes || typeof dims?.z !== 'number' || typeof dims?.y !== 'number') {
    return o;
  }
  const height = topLineY - floorY;
  const shift = (depth - dims.z) / 2;
  return {
    ...o,
    center: {
      x: o.center.x + shift * axes.wallNormal.x,
      y: floorY + height / 2,
      z: o.center.z + shift * axes.wallNormal.z,
    },
    dimensions: { ...dims, y: height, z: depth },
  };
}

/**
 * `objects` with each tall unit a run re-seats (`isReseatedTallUnit` — never
 * an oven column's section) at the nearest pantry width `family` makes,
 * keeping its centre. Set on the unit, not its bay: the engine re-sizes the
 * bay from the unit (`resolveRunBayFootprints`), so this moves the pantry, the
 * bay and the packing around it at once. A unit showing a product the catalog
 * never called scalable keeps its width, as `emitTallUnits` keeps it.
 */
function tallUnitsInLine(objects, runs, family) {
  const byId = new Map((objects || []).map((o) => [o?.id, o]));
  const ids = new Set();
  for (const run of runs || []) {
    for (const b of baysForRun(run)) {
      if (isReseatedTallUnit(b, byId.get(b?.applianceId))) {
        ids.add(b.applianceId);
      }
    }
  }
  const lineRun = { family: family.id };
  return (objects || []).map((o) =>
    ids.has(o?.id) && o.dimensions?.x > 0 && mayResizeObject(o)
      ? {
          ...o,
          dimensions: {
            ...o.dimensions,
            x: snapTallWidthToFamily(o.dimensions.x / IN, lineRun) * IN,
          },
        }
      : o
  );
}

/**
 * The top a tall body stands to under `family`: the height the fill stands a
 * tall span at below the run's upper line `topY` (`tallSpanSpec` — a vendor
 * family's tallest pantry that fits, else its shortest). A family with no
 * pantry heights answers the line itself.
 */
function tallTopInLine(floorY, topY, family) {
  const { height } = tallSpanSpec(floorY, {
    upperBand: { topY },
    tallHeightsIn: familyFillOpts({ family: family.id }).tallHeightsIn,
    cabinetDepth: STANDARD_CABINET_DEPTH,
  });
  return floorY + height;
}

/**
 * The look a run wears in the line: the one `familyFieldsForNewRun` gives it
 * (mapped from the run's own look, else the room's, into one the company
 * offers), as `lookForNewRun` reads it for a body built outside the engine.
 */
function lineLookOf(run, room, line) {
  const look = run?.appearance || room?.cabinetAppearance;
  return lookForNewRun(
    { cabinetAppearance: look },
    line.family.id,
    line.companyOffering
  );
}

/** A tall body as the line's pantry — a filler stays a filler. */
function asLinePantry(o) {
  if (o?.category?.filler) return o;
  const pantry = { ...o };
  applyBayType(pantry, 'tall');
  return pantry;
}

/** The ids of the cabinets the sinks among `objects` are seated in. */
function sinkHostIds(objects) {
  return new Set((objects || []).map(sinkHostIdOf).filter(Boolean));
}

/** A sink's host as the line's sink base. */
function asLineSinkBase(o) {
  return { ...o, category: { ...(o.category || {}), sinkBase: true } };
}

/**
 * A kept upper as the unit of its kind `family` would hang in its slot, or
 * null when the family makes none that fits (the fill hangs none). Its top
 * stays on its line, its back on its wall, its centre along the wall where it
 * was. Unchanged under a family with no size catalog, for a filler (it is cut
 * from stock whatever its width) and for a corner wall unit (its leg decides
 * its size, not this).
 */
function upperInLine(o, wall, family) {
  if (o?.category?.filler) return o;
  const kind = cabinetKindOf(family, o, { profileId: 'kitchen' });
  const dims = o?.dimensions;
  if (!SIZED_UPPER_KINDS.has(kind) || !dims) return o;
  const widthIn = fitUpperWidthIn(family, kind, dims.x / IN);
  const heightIn =
    widthIn == null
      ? null
      : snapUpperHeightIn(family, kind, widthIn, dims.y / IN);
  if (widthIn == null || heightIn == null) return null;
  const unit = unitsOf(family, kind).find(
    (u) => u.w === widthIn && u.h === heightIn
  );
  const depth = unit ? unit.d * IN : dims.z;
  const height = heightIn * IN;
  const width = widthIn * IN;
  const top = o.center.y + dims.y / 2;
  const axes = wall ? getWallAxes(wall) : null;
  const shift = axes ? (depth - dims.z) / 2 : 0;
  const scale = dims.x > 0 ? width / dims.x : 1;
  return {
    ...o,
    center: {
      x: o.center.x + (axes ? shift * axes.wallNormal.x : 0),
      y: top - height / 2,
      z: o.center.z + (axes ? shift * axes.wallNormal.z : 0),
    },
    dimensions: { ...dims, x: width, y: height, z: depth },
    ...(Array.isArray(o.columns)
      ? { columns: o.columns.map((c) => ({ ...c, width: c.width * scale })) }
      : {}),
  };
}

/**
 * ONE object of a re-solved run brought into the line — the rule both
 * callers apply object by object. `kept`: its id survived the re-solve (the
 * fill gives every box it builds a new one). `isSinkHost`: a sink is seated
 * in it. Returns the object, or null where the line makes no unit for a kept
 * upper's slot. Standing a tall body to its height is the caller's own rule
 * (`standardizedTallBody`), applied before this.
 */
function unitIntoLine(o, { family, look, wall, kept, isSinkHost }) {
  let out = o;
  if (kept && KEPT_IN_LINE_TYPES.has(o.type)) out = wearLook(out, look);
  if (isLineTallBody(o)) out = asLinePantry(out);
  if (isSinkHost) out = asLineSinkBase(out);
  if (kept && o.type === 'upper-cabinet' && !isOvenColumnPart(o)) {
    out = upperInLine(out, wall, family);
  }
  return out;
}

/**
 * The run's units `family`'s catalog cannot name — the sign it was built
 * before the line could size them. None for a family with no catalog. An
 * oven column's sections and the stock a line cuts accessories from are not
 * counted: neither is something a re-solve could change.
 */
function unitsOutsideLine(objects, runId, family) {
  if (!familySizes(family)) return [];
  return (objects || []).filter((o) => {
    if (o?.runId !== runId || !/cabinet|surround/.test(o.type || '')) {
      return false;
    }
    if (isOvenColumnPart(o)) return false;
    const r = resolveCabinetSku(family, o, { profileId: 'kitchen' });
    return !r.sku && !r.accessory;
  });
}

/** Does the run own any unit its line makes none of (`unitsOutsideLine`)? */
const runHoldsUnitsOutsideLine = (objects, runId, family) =>
  unitsOutsideLine(objects, runId, family).length > 0;

/**
 * The objects of a run just re-solved in `line` (`{ family, companyOffering }`),
 * with what the re-solve kept brought to the line's units. `before` is the
 * room as it stood (its objects decide what was KEPT), `run` the stored run.
 * Objects of other runs pass through untouched.
 */
function runUnitsIntoLine(before, objects, { run, line }) {
  const { family } = line;
  const keptIds = new Set(
    (before.objects || []).filter((o) => o?.runId === run.id).map((o) => o.id)
  );
  const wallById = new Map((before.walls || []).map((w) => [w.id, w]));
  const floorY = getFloorY(before);
  const ceilingY = roomCeilingY(before, floorY);
  const tallTop = tallTopInLine(
    floorY,
    columnTopY(upperBandFor(run, { floorY, ceilingY })),
    family
  );
  const look = lineLookOf(run, before, line);
  const hostIds = sinkHostIds(objects);
  return (objects || [])
    .map((o) => {
      if (o?.runId !== run.id) return o;
      const kept = keptIds.has(o.id);
      const wall = wallById.get((o.parentWallIds || [])[0]);
      // A kept tall unit stands to the line's height — and so does a tall
      // FILLER, whose height the fill reads off its tall neighbour BEFORE
      // that neighbour is stood up here: left alone it would step the run
      // at its own scribe (the generator's option stands every tall body).
      const stood =
        isLineTallBody(o) && (kept || o.category?.filler)
          ? standardizedTallBody(o, wall, floorY, tallTop)
          : o;
      return unitIntoLine(stood, {
        family,
        look,
        wall,
        kept,
        isSinkHost: hostIds.has(o.id),
      });
    })
    .filter(Boolean);
}

// Float noise between a size the line sells and the length a corner records
// it at — 33" stored in metres — not a design tolerance.
const L_SIZE_EPS = 0.001;

/**
 * The run with every recorded L corner the line does not make left for the
 * line to size.
 *
 * A derived run records what stands in each corner (`cornerTreatment.js`), an
 * L's leg included, and the fill builds a recorded L at that size as it is —
 * so a re-solve never re-sizes a corner the generator built. That holds while
 * the run stays in the line it was built in. Brought into another line, the
 * size is the OLD line's: a 29" L an earlier design stood at E3F4DC1B's
 * peninsula corner, built again under Venus, which sells 33" and 36". The
 * corner keeps its kind (an L); its `length` is dropped, so the fill builds
 * it at the 36" footprint every recorded L without a size is built at
 * (`planFromTreatment`) — a size every line sells. A size the line sells
 * (`cornerLSet`) is kept, and so is the USER's pick, which
 * rides its own field (`userTreatment`). Base corners only: an upper L is
 * sized by the upper tier's own rule (`upperTier.js`).
 */
function cornerSizesInLine(run) {
  const sizes = cornerLSet(run)?.sizes || [];
  let changed = false;
  const overrides = (run?.bayOverrides || []).map((o) => {
    const t = o?.treatment;
    if (tierOf(o) !== 'lower') return o;
    if (t?.mode !== 'cabinet' || !(t.length > 0)) return o;
    if (sizes.some((s) => Math.abs(s * IN - t.length) < L_SIZE_EPS)) return o;
    changed = true;
    const { length, ...rest } = t;
    return { ...o, treatment: rest };
  });
  return changed ? { ...run, bayOverrides: overrides } : run;
}

module.exports = {
  KEPT_IN_LINE_TYPES,
  cornerSizesInLine,
  unitsOutsideLine,
  isTallBody,
  isLineTallBody,
  standardizedTallBody,
  tallUnitsInLine,
  tallTopInLine,
  lineLookOf,
  asLinePantry,
  sinkHostIds,
  asLineSinkBase,
  upperInLine,
  unitIntoLine,
  runHoldsUnitsOutsideLine,
  runUnitsIntoLine,
};
